import React from 'react'
import app from '../../../feathers'
import { provideState, injectState, softUpdate } from 'freactal'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Tooltip } from 'react-mdl'
import Button from '../../../components/Button'
import {
  getSlides,
  sectionIndexToPath,
  hasLearnerGuideContent,
} from '../../../lib'
import CoverImageCard from '../components/CoverImageCard'
import ImageSelectionModal from '../../../components/ImageSelectionModal'
import BlockingSpinner from '../../components/BlockingSpinner'
import cloneDeep from 'lodash.clonedeep'

const sleep = (time = 0) => new Promise(res => setTimeout(res, time))

const Container = styled.div`
  height: 75vh;
  overflow-y: auto;
`
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const withState = provideState({
  initialState: () => ({ addImageError: null, loading: null }),
  computed: {
    rootCoverImage: ({ unit: { presentation: { coverImage = null } = {} } }) =>
      coverImage,
    coverImages: ({ unit: { sections } }) =>
      sections.map(
        ({ presentation: { coverImage = null } = {} }) => coverImage,
      ),
  },
  effects: {
    updateImage: async (effects, { unitId, sectionIndex, image }) => {
      effects.setLoading('Updating Image')
      const path = sectionIndex != null ? `sections.${sectionIndex}.` : ''
      try {
        await app.service('units').patch(unitId, {
          [`${path}presentation.coverImage`]: image,
        })
      } catch (addImageError) {
        return state => ({ ...state, addImageError })
      } finally {
        effects.setLoading(null)
      }
    },
    setLoading: softUpdate((_, loading) => ({ loading })),
  },
})

class PresentationSettings extends React.Component {
  static contextTypes = {
    prompt: PropTypes.func,
  }

  changeImage = sectionIndex => async () => {
    const { state: { unit }, effects: { updateImage } } = this.props
    const scopeContent = unit.title
    const response = await this.context.prompt({
      ContentComponent: props => (
        <ImageSelectionModal
          {...props}
          unitImages={unit.images || []}
          imageLimit={1}
          scopeContent={scopeContent}
        />
      ),
      style: {
        top: '2em',
        width: 850,
      },
    })
    if (response === 'cancel') return
    const [image] = response

    updateImage({ unitId: unit._id, sectionIndex, image })
  }

  addSlidesFromLearnerGuideContent = async () => {
    const {
      state: { unit },
      effects: { initializeSlides, setLoading },
    } = this.props

    await setLoading('Initialising Slides')

    const emptyParagraph = {
      ranges: [],
      nodes: [
        {
          ranges: [
            {
              marks: [],
              text: '',
              kind: 'range',
            },
          ],
          nodes: [],
          kind: 'text',
        },
      ],
      type: 'paragraph',
      kind: 'block',
    }

    const slideData = content => {
      const slideObj = {}
      const newNodes = content.nodes.reduce((nodes, node) => {
        switch (node.type) {
          case 'media_block':
            // use the first image associated with a section
            if (slideObj.image == null) {
              const image = node.data.images[0]
              slideObj.image = {
                ...image,
                display: {
                  ...image.display,
                  width: 5.5,
                  align: 'right',
                },
              }
            }
            // add the content associated with the media_block
            nodes.push(...node.nodes)
            break
          case 'image':
            if (slideObj.image != null) break
            slideObj.image = {
              ...node.data,
              display: {
                ...node.data.display,
                width: 5.5,
                align: 'right',
              },
            }
            break
          default:
            nodes.push(node)
        }
        return nodes
      }, [])

      // parse slide nodes to unwrap table content from table
      const removeTables = nodes =>
        nodes.reduce((nodes, node) => {
          // extract content from table cells
          if (node.type === 'table') {
            // get table rows from table
            node.nodes.forEach(({ nodes: tableRowNodes }) =>
              // get table cells from the rows
              tableRowNodes.forEach(({ nodes: tableCellNodes }) =>
                // add processed cell content to nodes
                nodes.push(
                  // process cell content to remove color marks
                  ...tableCellNodes.map(cellNode => {
                    if (cellNode.type === 'paragraph') {
                      // extract ranges from paragraph
                      cellNode.nodes.forEach(({ ranges }) =>
                        ranges.forEach(
                          range =>
                            // remove color marks
                            (range.marks = range.marks.filter(
                              ({ type }) => type !== 'color',
                            )),
                        ),
                      )
                    }
                    return cellNode
                  }),
                  // add an empty paragraph to nodes after cell content to separate cells
                  emptyParagraph,
                ),
              ),
            )
          } else {
            nodes.push(node)
          }
          return nodes
        }, [])

      const notesNodes = cloneDeep(newNodes)
      slideObj.content = { nodes: newNodes }
      slideObj.notes = { nodes: removeTables(notesNodes) }
      return slideObj
    }

    const getSectionsMap = (sections, rootSectionPath) => {
      return sections.reduce((sectionsMap, section, sectionIndex) => {
        const sectionPath = rootSectionPath
          ? `${rootSectionPath}.${sectionIndex}`
          : `${sectionIndex}`
        // skip top level sections
        if (rootSectionPath != null) {
          const slide = slideData(section.content)
          sectionsMap[
            `sections.${sectionIndexToPath(sectionPath)}.presentation.slides`
            // TODO: Create only one slide when duplicate slide is fixed.
          ] = [slide, slide, slide]
        }

        if (section.subsections) {
          return {
            ...sectionsMap,
            ...getSectionsMap(section.subsections, sectionPath),
          }
        }

        return sectionsMap
      }, {})
    }

    const sectionsMap = getSectionsMap(unit.sections)
    await initializeSlides({ _id: unit._id, sectionsMap })
    await sleep(10000)
    await setLoading(null).then(
      this.props.history.push(`/units/${unit._id}/presentation/0.0/0/`),
    )
  }

  render() {
    const {
      state: { unit, rootCoverImage, coverImages, loading },
      effects: { snackbarAlert },
    } = this.props
    const hasSlides = getSlides(unit, 'presentation').length !== 0

    return (
      <div>
        <Container>
          <h3>Presentation Settings</h3>
          <CoverImageCard image={rootCoverImage} onClick={this.changeImage()} />
          {coverImages.map((coverImage, i) => (
            <CoverImageCard
              key={i}
              image={coverImage}
              sectionNumber={i + 1}
              sectionName={unit.sections[i].name}
              onClick={this.changeImage(i)}
            />
          ))}
        </Container>
        <ButtonContainer>
          <Tooltip
            position="top"
            large={true}
            label="Create slides from Learner Guide content">
            <Button
              disabled={hasSlides || hasLearnerGuideContent(unit).length > 0}
              onDisabledClick={e => {
                e.preventDefault()
                const alertMessage = hasSlides
                  ? 'Slides already initialised for this unit.'
                  : 'Please ensure that all Learner Guide sections have content'
                snackbarAlert(alertMessage)
              }}
              primary
              onClick={this.addSlidesFromLearnerGuideContent}>
              Initialise Slides
            </Button>
          </Tooltip>
        </ButtonContainer>
        {loading && (
          <BlockingSpinner>
            <h3>{loading}</h3>
          </BlockingSpinner>
        )}
      </div>
    )
  }
}

export default withState(injectState(PresentationSettings))



// WEBPACK FOOTER //
// ./src/Units/ManageUnit/PresentationContent/PresentationSettings.js